import { API } from '@/lib/api';
import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Star, MessageCircle, CheckCircle2, MapPin, Clock, Shield, Loader2, RefreshCw, ArrowLeft, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import Navbar from "@/components/layout/Navbar";



const MatchingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const bookingData = location.state;

  const [phase, setPhase] = useState("searching"); // searching | matched | expired | failed
  const [matchedPro, setMatchedPro] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [nearbyCount, setNearbyCount] = useState(0);
  const [searchDots, setSearchDots] = useState(0);
  const pollRef = useRef(null);
  const requestedRef = useRef(false);

  // Animate dots
  useEffect(() => {
    if (phase !== "searching") return;
    const i = setInterval(() => setSearchDots(d => (d + 1) % 4), 500);
    return () => clearInterval(i);
  }, [phase]);

  // Create match request on mount
  useEffect(() => {
    if (!bookingData) { navigate("/", { replace: true }); return; }
    if (requestedRef.current) return;
    requestedRef.current = true;
    createRequest();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const createRequest = async () => {
    setPhase("searching");
    try {
      const res = await axios.post(`${API}/match/request`, {
        booking_id: bookingData.booking_id,
        booking_type: bookingData.booking_type,
        service_type: bookingData.service_type,
        pickup_lat: bookingData.pickup_lat,
        pickup_lng: bookingData.pickup_lng,
        pickup_address: bookingData.pickup_address || "",
        dropoff_address: bookingData.dropoff_address || "",
        vehicle_type: bookingData.vehicle_type || "",
        roadside_sub_service: bookingData.roadside_sub_service || "",
        laborer_job_type: bookingData.laborer_job_type || "",
      }, { withCredentials: true });

      if (res.data.request_id) {
        setRequestId(res.data.request_id);
        setNearbyCount(res.data.nearby_professionals || 0);
        startPolling(res.data.request_id);
      } else {
        setPhase("failed");
      }
    } catch {
      setPhase("failed");
    }
  };

  const startPolling = (reqId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/match/status/${reqId}`, { withCredentials: true });
        if (res.data.status === "accepted") {
          clearInterval(pollRef.current);
          setMatchedPro(res.data.professional);
          setPhase("matched");
        } else if (res.data.status === "expired") {
          clearInterval(pollRef.current);
          setPhase("expired");
        }
      } catch {
        // keep polling
      }
    }, 3000);
  };

  const retry = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    createRequest();
  };

  if (!bookingData) return null;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="matching-page">
      <Navbar />
      <div className="pt-24 pb-16 px-4 flex items-center justify-center min-h-screen">
        <div className="w-full max-w-md">

          {/* SEARCHING PHASE */}
          {phase === "searching" && (
            <div className="text-center" data-testid="matching-searching">
              <div className="relative w-40 h-40 mx-auto mb-8">
                {[0, 1, 2].map(i => (
                  <div key={i} className="absolute inset-0 rounded-full border-2 border-emerald-400 opacity-0" style={{ animation: "matchPulse 3s ease-out infinite", animationDelay: `${i}s` }} />
                ))}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-emerald-950 rounded-full flex items-center justify-center shadow-xl">
                    <MapPin className="w-9 h-9 text-lime-400" />
                  </div>
                </div>
              </div>

              <h2 className="text-xl font-bold text-emerald-950 mb-2">
                Looking for a professional{".".repeat(searchDots)}
              </h2>
              <p className="text-sm text-stone-500 mb-2">
                Waiting for a nearby professional to accept your request
              </p>
              {nearbyCount > 0 && (
                <div className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium px-3 py-1.5 rounded-full mb-6" data-testid="nearby-count">
                  <Users className="w-3.5 h-3.5" />
                  {nearbyCount} professional{nearbyCount > 1 ? "s" : ""} nearby
                </div>
              )}

              <div className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full mt-1.5 animate-pulse" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-emerald-950">{bookingData.pickup_address}</p>
                    {bookingData.dropoff_address && (
                      <>
                        <div className="w-px h-4 bg-stone-200 ml-1 my-1" />
                        <p className="text-sm text-stone-600">{bookingData.dropoff_address}</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* MATCHED PHASE */}
          {phase === "matched" && matchedPro && (
            <div className="text-center" data-testid="matching-matched">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-9 h-9 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-emerald-950 mb-1">Professional Found!</h2>
              <p className="text-sm text-stone-500 mb-6">Your booking has been confirmed</p>

              <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden mb-4" data-testid="matched-pro-card">
                <div className="bg-emerald-950 px-5 py-4 flex items-center gap-4">
                  <div className="w-14 h-14 bg-lime-400 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-950 text-xl font-bold">{matchedPro.name?.charAt(0)?.toUpperCase()}</span>
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <p className="text-white font-semibold text-lg truncate" data-testid="pro-name">{matchedPro.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {matchedPro.avg_rating > 0 && (
                        <div className="flex items-center gap-1">
                          <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                          <span className="text-white text-sm font-medium">{matchedPro.avg_rating.toFixed(1)}</span>
                          <span className="text-stone-400 text-xs">({matchedPro.review_count})</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1 text-emerald-300">
                        <Shield className="w-3 h-3" />
                        <span className="text-xs">Verified</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="px-5 py-4 space-y-3">
                  {matchedPro.eta_minutes && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-stone-600"><Clock className="w-4 h-4" /><span className="text-sm">Estimated arrival</span></div>
                      <span className="font-semibold text-emerald-700" data-testid="pro-eta">{matchedPro.eta_minutes} min</span>
                    </div>
                  )}
                  {matchedPro.distance_km && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-stone-600"><MapPin className="w-4 h-4" /><span className="text-sm">Distance</span></div>
                      <span className="font-semibold text-stone-800">{matchedPro.distance_km} km away</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-stone-200 p-4 shadow-sm mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full mt-1.5" />
                  <div className="text-left flex-1">
                    <p className="text-xs text-stone-400 uppercase tracking-wide">Pickup</p>
                    <p className="text-sm font-medium text-emerald-950">{bookingData.pickup_address}</p>
                    {bookingData.dropoff_address && (
                      <>
                        <div className="w-px h-3 bg-stone-200 ml-1 my-1" />
                        <p className="text-xs text-stone-400 uppercase tracking-wide">Drop-off</p>
                        <p className="text-sm font-medium text-stone-700">{bookingData.dropoff_address}</p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-12 text-base gap-2 mb-3" onClick={() => navigate(`/tracking/${requestId}`, { state: { professional: matchedPro, bookingData } })} data-testid="track-live-btn">
                <MapPin className="w-5 h-5" /> Track {matchedPro.name?.split(" ")[0]} Live
              </Button>
              <Button variant="outline" className="w-full rounded-xl h-11 text-sm gap-2 mb-3" onClick={() => navigate(`/chat/${requestId}`, { state: { professional: matchedPro, bookingData } })} data-testid="chat-with-pro-btn">
                <MessageCircle className="w-5 h-5" /> Chat with {matchedPro.name?.split(" ")[0]}
              </Button>
              <Button variant="outline" className="w-full rounded-xl h-11 text-sm" onClick={() => navigate("/my-bookings")} data-testid="view-bookings-btn">
                View My Bookings
              </Button>
            </div>
          )}

          {/* EXPIRED / FAILED */}
          {(phase === "expired" || phase === "failed") && (
            <div className="text-center" data-testid="matching-failed">
              <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-8 h-8 text-stone-400" />
              </div>
              <h2 className="text-xl font-bold text-emerald-950 mb-2">
                {phase === "expired" ? "Request Expired" : "No Professionals Available"}
              </h2>
              <p className="text-sm text-stone-500 mb-6">
                {phase === "expired"
                  ? "No professional accepted in time. Let's try again."
                  : "All professionals are currently busy. Please try again in a moment."}
              </p>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-12 gap-2 mb-3" onClick={retry} data-testid="retry-match-btn">
                <RefreshCw className="w-4 h-4" /> Try Again
              </Button>
              <Button variant="outline" className="w-full rounded-xl h-11 text-sm" onClick={() => navigate(-1)} data-testid="go-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Go Back
              </Button>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes matchPulse { 0% { transform: scale(0.5); opacity: 0.8; } 100% { transform: scale(2); opacity: 0; } }`}</style>
    </div>
  );
};

export default MatchingPage;
